import { InputAdornment, Typography } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import FormGroup from '@mui/material/FormGroup';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import makeStyles from '@mui/styles/makeStyles';
import {
	DesktopDateTimePicker,
	LocalizationProvider,
} from '@mui/x-date-pickers';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { CalendarOrClockPickerView } from '@mui/x-date-pickers/internals/models';
import dayjs from 'dayjs';
import _ from 'lodash';
import React from 'react';
import { AuthData, withAuthContext } from '../../context/auth';
import {
	DATE_PRECISION,
	DATE_PRECISION_INPUT_FORMAT,
	DATE_PRECISION_PLACEHOLDER,
	DATE_PRECISION_VIEWS,
} from '../fastForm/data/constants';
import { dateConversion } from '../fastForm/utils';
import FieldLabel from '../fields/FieldLabel';

// Simple example for how to use the onChange api:
// setDate is a useState setter function.
// Allow setting invalid dates so that keyboard input will work. Setting to null or ''
// will blank out the input box
// const onChange = (date: Date, value?: string) => { setDate(date) };

// For error messates - the initial date on page load should be set to null if the error message needs to be hidden on
// page load. An empty string('') value represents a blank value after user has used the keyboard input.
interface Props {
	value: DefaultDatePickerValue;
	onChange: (date: DefaultDatePickerValue, value?: string | null) => void;
	label: string;
	minDate?: any;
	maxDate?: any;
	precision?: string;
	initialFocusedDate?: Date;
	required?: boolean;
	disabled?: boolean;
	fullWidth?: boolean;
	disableFuture?: boolean;
	error?: boolean;
	errorMessage?: string;
	style?: any;
	id?: string;
	helperText?: string;
	showCalendar?: boolean;
	openDialogAriaText?: string;
	authData: AuthData;
	disableTimePicker?: boolean;
}

export type DefaultDatePickerValue = Date | '';

const useStyles = makeStyles((theme) => ({
	labelAsterisk: {
		color: 'red',
	},
	disabledInput: {
		'& .MuiInputBase-root.Mui-disabled': {
			color: 'rgba(0, 0, 0, 0.54)',
		},
	},
	disabledCalendarInput: {
		'& .MuiInputAdornment-root.MuiInputAdornment-positionEnd': {
			display: 'none',
		},
	},
}));

const DefaultDatePicker = (props: Props) => {
	const {
		value,
		onChange,
		label,
		minDate,
		maxDate,
		initialFocusedDate,
		required,
		precision,
		disableTimePicker,
		disabled,
		disableFuture,
		error,
		errorMessage,
		style,
		id,
		helperText,
		showCalendar,
		authData,
		openDialogAriaText,
	} = props;
	const classes = useStyles();
	const isValueNull = value === null;
	const timezone = authData.auth.timezone;

	const convertDate = (date: dayjs.Dayjs, val: string) => {
		if (date === null) {
			onChange('', val);
			return;
		}
		const dayJsObj = dateConversion.dayJsObjectToDateFormString(
			date,
			precision ? precision : DATE_PRECISION.DAY,
		);
		const zonedTimestamp = dateConversion.convertDateStringToZonedTimestamp(
			dayJsObj,
			precision ? precision : DATE_PRECISION.DAY,
			timezone,
		);
		const parsedDate = new Date(parseInt(zonedTimestamp, 10));
		onChange(parsedDate, val);
	};

	const convertedDateFormat = (date: DefaultDatePickerValue) => {
		if (!date) {
			return null;
		}
		const parsedDateString = dayjs(date)
			.tz(timezone)
			.format(
				DATE_PRECISION_INPUT_FORMAT[precision ? precision : DATE_PRECISION.DAY],
			);
		const parsedDate = dayjs(parsedDateString);
		const isDayJs = dayjs.isDayjs(parsedDate);
		if (isDayJs) {
			return parsedDate;
		}
		return null;
	};

	const getOpenDialogAriaText = (value: any, utils: any) => {
		if (!value) {
			return !!openDialogAriaText ? openDialogAriaText : 'Choose date';
		}

		return !!openDialogAriaText
			? `${openDialogAriaText}, selected date is ${utils.format(
					utils.date(value),
					'fullDate',
			  )}`
			: `Choose date, selected date is ${utils.format(
					utils.date(value),
					'fullDate',
			  )}`;
	};
	const dayJsInitialFocusedDate = dayjs(initialFocusedDate);
	const dayJsMinDate = dayjs(minDate);
	const dayJsMaxDate = dayjs(maxDate);
	const dayJsValue = convertedDateFormat(value);
	const validInitialFocusedDate = dayJsInitialFocusedDate.isValid();

	const endAdornment = (
		<InputAdornment position='end'>
			<Typography sx={{ color: disabled ? 'text.disabled' : 'inherit' }}>
				{dateConversion.getZoneNameAbbreviation(timezone)}
			</Typography>
		</InputAdornment>
	);

	let views = [] as CalendarOrClockPickerView[];
	let openTo = 'day' as CalendarOrClockPickerView;
	const dateTimePrecisionValues = precision
		? DATE_PRECISION_VIEWS[precision]
		: DATE_PRECISION_VIEWS[DATE_PRECISION.DAY];

	if (precision === DATE_PRECISION.YEAR || precision === DATE_PRECISION.MONTH) {
		views = [...dateTimePrecisionValues];
		openTo = 'year';
	} else if (precision === DATE_PRECISION.DAY) {
		views = [...dateTimePrecisionValues];
		openTo = 'day';
	} else if (
		precision === DATE_PRECISION.HOUR ||
		precision === DATE_PRECISION.MINUTE
	) {
		views = !disableTimePicker
			? [...dateTimePrecisionValues]
			: DATE_PRECISION_VIEWS[DATE_PRECISION.DAY]; // ['year', 'month', 'day']
		openTo = 'day';
	} else if (precision === DATE_PRECISION.SECOND) {
		views = !disableTimePicker
			? DATE_PRECISION_VIEWS[DATE_PRECISION.HOUR] // ['year', 'month', 'day', 'hours']
			: DATE_PRECISION_VIEWS[DATE_PRECISION.DAY];
		openTo = 'day';
	} else {
		views = DATE_PRECISION_VIEWS[DATE_PRECISION.DAY];
	}

	return (
		<LocalizationProvider dateAdapter={AdapterDayjs}>
			<FormControl error={!isValueNull && error} style={style}>
				<div style={{ display: 'flex', flexDirection: 'column' }}>
					<FormGroup
						className={showCalendar ? '' : classes.disabledCalendarInput}
					>
						<DesktopDateTimePicker
							value={dayJsValue}
							views={views}
							openTo={openTo}
							disabled={disabled}
							disableFuture={disableFuture}
							minDate={minDate ? dayJsMinDate : undefined}
							maxDate={maxDate ? dayJsMaxDate : undefined}
							inputFormat={
								precision
									? DATE_PRECISION_INPUT_FORMAT[precision]
									: DATE_PRECISION_INPUT_FORMAT[DATE_PRECISION.DAY]
							}
							onChange={convertDate}
							defaultCalendarMonth={
								validInitialFocusedDate ? dayJsInitialFocusedDate : undefined
							}
							getOpenDialogAriaText={getOpenDialogAriaText}
							renderInput={(params: TextFieldProps) => (
								<FormControl
									sx={{
										width: '100%',
									}}
								>
									<FieldLabel
										title={label}
										required={required}
										htmlFor={label}
									/>
									<TextField
										{...params}
										id={_.camelCase(id)}
										fullWidth
										error={!!error}
										variant='standard'
										inputProps={{
											...params.inputProps,
											placeholder: precision
												? DATE_PRECISION_PLACEHOLDER[precision]
												: DATE_PRECISION_PLACEHOLDER[DATE_PRECISION.DAY],
										}}
										InputProps={{
											endAdornment: (
												<>
													{(precision === DATE_PRECISION.HOUR ||
														precision === DATE_PRECISION.MINUTE ||
														precision === DATE_PRECISION.SECOND) &&
														endAdornment}
													{showCalendar && params.InputProps?.endAdornment}
												</>
											),
										}}
										helperText={
											!isValueNull && errorMessage
												? errorMessage
												: helperText && !errorMessage
												? helperText
												: null
										}
									/>
								</FormControl>
							)}
						/>
					</FormGroup>
				</div>
			</FormControl>
		</LocalizationProvider>
	);
};

export default withAuthContext<Props>(DefaultDatePicker);
